// ═══════════════════════════════════════════
// PeakWave Season Presets — Region-Aware
// Auto-generates rolling 18-month windows
// ═══════════════════════════════════════════

export interface RegionGroup {
  region: string;
  type: "winter" | "summer" | "year_round";
  displayGroup: "snow" | "sun" | "festival";
  description: string;
  isPrimary: boolean;
  presets: SeasonPreset[];
}

export interface SeasonPreset {
  label: string;
  region: string;
  type: "winter" | "summer" | "year_round";
  displayGroup: "snow" | "sun" | "festival";
  from: string; // ISO date "2026-12-01"
  to: string; // ISO date "2027-04-30"
}

// ═══ REGION TEMPLATES ═══

interface RegionTemplate {
  region: string;
  type: "winter" | "summer" | "year_round";
  displayGroup: "snow" | "sun" | "festival";
  description: string;
  isPrimary: boolean;
  labelPattern: string;
  fromMonth: number; // 1-12
  fromDay: number;
  toMonth: number; // 1-12
  toDay: number;
}

const REGION_TEMPLATES: RegionTemplate[] = [
  // ── Primary regions ──
  {
    region: "European Alps",
    type: "winter",
    displayGroup: "snow",
    description: "Chalets, ski schools & mountain hospitality",
    isPrimary: true,
    labelPattern: "Alps Winter",
    fromMonth: 12,
    fromDay: 1,
    toMonth: 4,
    toDay: 30,
  },
  {
    region: "North America",
    type: "winter",
    displayGroup: "snow",
    description: "Ski resorts across the Rockies, BC & East Coast",
    isPrimary: true,
    labelPattern: "North America Winter",
    fromMonth: 11,
    fromDay: 15,
    toMonth: 4,
    toDay: 15,
  },
  {
    region: "Mediterranean",
    type: "summer",
    displayGroup: "sun",
    description: "Yachting, watersports & beach clubs",
    isPrimary: true,
    labelPattern: "Med Summer",
    fromMonth: 5,
    fromDay: 1,
    toMonth: 10,
    toDay: 31,
  },
  {
    region: "Caribbean & Tropics",
    type: "winter",
    displayGroup: "sun",
    description: "Yacht charters, dive centres & island resorts",
    isPrimary: true,
    labelPattern: "Caribbean",
    fromMonth: 11,
    fromDay: 1,
    toMonth: 4,
    toDay: 30,
  },
  // ── Other regions ──
  {
    region: "Japan",
    type: "winter",
    displayGroup: "snow",
    description: "Niseko, Hakuba & Japanese ski resorts",
    isPrimary: false,
    labelPattern: "Japan Winter",
    fromMonth: 12,
    fromDay: 1,
    toMonth: 3,
    toDay: 31,
  },
  {
    region: "Southern Hemisphere",
    type: "winter",
    displayGroup: "snow",
    description: "NZ, Australia & South America ski",
    isPrimary: false,
    labelPattern: "Southern Hemisphere Winter",
    fromMonth: 6,
    fromDay: 1,
    toMonth: 9,
    toDay: 30,
  },
  {
    region: "Scandinavia",
    type: "winter",
    displayGroup: "snow",
    description: "Nordic ski resorts & lodges",
    isPrimary: false,
    labelPattern: "Scandinavia Winter",
    fromMonth: 11,
    fromDay: 1,
    toMonth: 4,
    toDay: 30,
  },
  {
    region: "Australia & Pacific",
    type: "summer",
    displayGroup: "sun",
    description: "Watersports, beach clubs & island resorts",
    isPrimary: false,
    labelPattern: "Australia Summer",
    fromMonth: 11,
    fromDay: 1,
    toMonth: 3,
    toDay: 31,
  },
  {
    region: "Southeast Asia",
    type: "year_round",
    displayGroup: "sun",
    description: "Dive centres, watersports & island resorts",
    isPrimary: false,
    labelPattern: "SE Asia",
    fromMonth: 1,
    fromDay: 1,
    toMonth: 12,
    toDay: 31,
  },
  {
    region: "North America Summer",
    type: "summer",
    displayGroup: "sun",
    description: "Summer camps, beach towns, national parks & resorts",
    isPrimary: false,
    labelPattern: "North America Summer",
    fromMonth: 5,
    fromDay: 15,
    toMonth: 9,
    toDay: 15,
  },
  {
    region: "Middle East & Gulf",
    type: "winter",
    displayGroup: "sun",
    description: "Superyacht bases, luxury hospitality & private estates",
    isPrimary: false,
    labelPattern: "Middle East",
    fromMonth: 10,
    fromDay: 1,
    toMonth: 5,
    toDay: 31,
  },
  // ── Festival regions ──
  {
    region: "UK Festivals",
    type: "summer",
    displayGroup: "festival",
    description: "Music festivals, sporting events & outdoor shows",
    isPrimary: false,
    labelPattern: "UK Festivals",
    fromMonth: 5,
    fromDay: 1,
    toMonth: 9,
    toDay: 30,
  },
  {
    region: "European Festivals",
    type: "summer",
    displayGroup: "festival",
    description: "European music festivals, events & touring circuits",
    isPrimary: false,
    labelPattern: "Europe Festivals",
    fromMonth: 5,
    fromDay: 1,
    toMonth: 9,
    toDay: 30,
  },
  {
    region: "North American Festivals",
    type: "summer",
    displayGroup: "festival",
    description: "US & Canadian festivals, fairs & outdoor events",
    isPrimary: false,
    labelPattern: "North America Festivals",
    fromMonth: 4,
    fromDay: 1,
    toMonth: 10,
    toDay: 31,
  },
  {
    region: "Australasia Festivals",
    type: "summer",
    displayGroup: "festival",
    description: "Australian & NZ festivals, events & summer circuits",
    isPrimary: false,
    labelPattern: "Australasia Festivals",
    fromMonth: 11,
    fromDay: 1,
    toMonth: 3,
    toDay: 31,
  },
];

// ═══ HELPERS ═══

function pad(n: number): string {
  return n.toString().padStart(2, "0");
}

function toISO(year: number, month: number, day: number): string {
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** Does this template's date range cross a year boundary? (fromMonth > toMonth) */
function crossesYear(t: RegionTemplate): boolean {
  return t.fromMonth > t.toMonth;
}

function formatLabel(pattern: string, startYear: number, crossYear: boolean): string {
  if (crossYear) {
    const endYearShort = pad((startYear + 1) % 100);
    return `${pattern} ${startYear}/${endYearShort}`;
  }
  return `${pattern} ${startYear}`;
}

/**
 * Typical end date ("YYYY-MM-DD") of a generated season label such as
 * "Alps Winter 2026/27" or "UK Festivals 2026", or null for labels that do
 * not come from a region template.
 */
export function seasonLabelTypicalEnd(label: string): string | null {
  const trimmed = label.trim();
  for (const template of REGION_TEMPLATES) {
    const prefix = `${template.labelPattern} `;
    if (!trimmed.startsWith(prefix)) continue;
    const rest = trimmed.slice(prefix.length);
    const match = /^(\d{4})(?:\/(\d{2}))?$/u.exec(rest);
    if (!match) continue;
    const startYear = Number(match[1]);
    const crosses = crossesYear(template);
    if (crosses !== Boolean(match[2])) continue;
    return toISO(crosses ? startYear + 1 : startYear, template.toMonth, template.toDay);
  }
  return null;
}

function generatePresetsForTemplate(
  template: RegionTemplate,
  now: Date,
  horizon: Date
): SeasonPreset[] {
  const presets: SeasonPreset[] = [];
  const crosses = crossesYear(template);
  // `to` is the season's inclusive last day, compared as a UTC date.
  const today = now.toISOString().slice(0, 10);

  // Check years from 1 year before now to 2 years after
  const startCheckYear = now.getFullYear() - 1;
  const endCheckYear = now.getFullYear() + 2;

  for (let year = startCheckYear; year <= endCheckYear; year++) {
    const fromYear = year;
    const toYear = crosses ? year + 1 : year;
    const from = toISO(fromYear, template.fromMonth, template.fromDay);
    const to = toISO(toYear, template.toMonth, template.toDay);

    const fromDate = new Date(from);

    // Include if: the season runs through today or later AND it starts within the horizon
    if (to >= today && fromDate <= horizon) {
      presets.push({
        label: formatLabel(template.labelPattern, year, crosses),
        region: template.region,
        type: template.type,
        displayGroup: template.displayGroup,
        from,
        to,
      });
    }
  }

  return presets;
}

// ═══ SEASON TYPES ═══

export interface SeasonType {
  id: string;
  label: string;
  type: "winter" | "summer" | "year_round";
  displayGroup: "snow" | "sun" | "festival";
  /** Region template names that map to this season type */
  regions: string[];
}

export const SEASON_TYPES: SeasonType[] = [
  {
    id: "alpine-winter",
    label: "Alpine Winter",
    type: "winter",
    displayGroup: "snow",
    regions: ["European Alps", "Scandinavia"],
  },
  {
    id: "na-winter",
    label: "North America Winter",
    type: "winter",
    displayGroup: "snow",
    regions: ["North America"],
  },
  {
    id: "japan-winter",
    label: "Japan Winter",
    type: "winter",
    displayGroup: "snow",
    regions: ["Japan"],
  },
  {
    id: "southern-winter",
    label: "Southern Hemisphere Winter",
    type: "winter",
    displayGroup: "snow",
    regions: ["Southern Hemisphere"],
  },
  {
    id: "med-summer",
    label: "Med Summer",
    type: "summer",
    displayGroup: "sun",
    regions: ["Mediterranean"],
  },
  {
    id: "caribbean",
    label: "Caribbean & Tropics",
    type: "winter",
    displayGroup: "sun",
    regions: ["Caribbean & Tropics"],
  },
  {
    id: "aus-summer",
    label: "Australia & Pacific Summer",
    type: "summer",
    displayGroup: "sun",
    regions: ["Australia & Pacific"],
  },
  {
    id: "se-asia",
    label: "Southeast Asia",
    type: "year_round",
    displayGroup: "sun",
    regions: ["Southeast Asia"],
  },
  {
    id: "na-summer",
    label: "North America Summer",
    type: "summer",
    displayGroup: "sun",
    regions: ["North America Summer"],
  },
  {
    id: "middle-east",
    label: "Middle East & Gulf",
    type: "winter",
    displayGroup: "sun",
    regions: ["Middle East & Gulf"],
  },
  {
    id: "uk-festivals",
    label: "UK Festivals",
    type: "summer",
    displayGroup: "festival",
    regions: ["UK Festivals"],
  },
  {
    id: "eu-festivals",
    label: "European Festivals",
    type: "summer",
    displayGroup: "festival",
    regions: ["European Festivals"],
  },
  {
    id: "na-festivals",
    label: "North America Festivals",
    type: "summer",
    displayGroup: "festival",
    regions: ["North American Festivals"],
  },
  {
    id: "aus-festivals",
    label: "Australasia Festivals",
    type: "summer",
    displayGroup: "festival",
    regions: ["Australasia Festivals"],
  },
];

/** Get active season presets for a given season type ID */
export function getActiveSeasonPresetsForType(seasonTypeId: string): SeasonPreset[] {
  const st = SEASON_TYPES.find((s) => s.id === seasonTypeId);
  if (!st) return [];
  const allSeasons = getAvailableSeasons();
  return allSeasons
    .filter((g) => st.regions.includes(g.region))
    .flatMap((g) => g.presets);
}

// ═══ PUBLIC API ═══

/** Get all available seasons grouped by region, auto-generated for rolling 18 months */
export function getAvailableSeasons(): RegionGroup[] {
  const now = new Date();
  const horizon = new Date(now);
  horizon.setMonth(horizon.getMonth() + 18);

  const groups: RegionGroup[] = [];

  // Primary regions first, then others
  const sorted = [...REGION_TEMPLATES].sort((a, b) => {
    if (a.isPrimary && !b.isPrimary) return -1;
    if (!a.isPrimary && b.isPrimary) return 1;
    return 0;
  });

  for (const template of sorted) {
    const presets = generatePresetsForTemplate(template, now, horizon);
    if (presets.length > 0) {
      groups.push({
        region: template.region,
        type: template.type,
        displayGroup: template.displayGroup,
        description: template.description,
        isPrimary: template.isPrimary,
        presets,
      });
    }
  }

  return groups;
}

/** Alias for getAvailableSeasons */
export function getRegions(): RegionGroup[] {
  return getAvailableSeasons();
}

/** Find a preset by its exact label */
export function getPresetByLabel(label: string): SeasonPreset | undefined {
  const groups = getAvailableSeasons();
  for (const group of groups) {
    const found = group.presets.find((p) => p.label === label);
    if (found) return found;
  }
  return undefined;
}

/** Get just the primary region presets as a flat list (for signup pills) */
export function getPrimaryPresets(): SeasonPreset[] {
  return getAvailableSeasons()
    .filter((g) => g.isPrimary)
    .flatMap((g) => g.presets);
}

/** Check whether a new date range overlaps with any existing windows */
export function checkDateOverlap(
  existingWindows: {
    available_from: string;
    available_to: string;
    id?: string;
    season_label?: string;
  }[],
  newFrom: string,
  newTo: string,
  excludeId?: string
): { overlaps: boolean; conflictingLabel?: string } {
  const nf = new Date(newFrom);
  const nt = new Date(newTo);

  for (const w of existingWindows) {
    if (excludeId && w.id === excludeId) continue;
    const wf = new Date(w.available_from);
    const wt = new Date(w.available_to);

    // Two ranges overlap if: start1 < end2 AND start2 < end1
    if (nf < wt && wf < nt) {
      return { overlaps: true, conflictingLabel: w.season_label };
    }
  }

  return { overlaps: false };
}
